import SectionHeader from "@/components/SectionHeader";
import { useEffect, useState } from "react";
import { IMG, api } from "@/lib/api";
import { Link } from "react-router-dom";
import { ArrowUpRight, Download, FileText, Map, Building2, Briefcase, MessageCircle, Lock, Sparkles } from "lucide-react";

const PODCASTS = [
  {
    title: "The Millionaire Real Estate Agent",
    host: "The MREA Podcast",
    desc: "Built from Gary Keller's classic. Frameworks, scripts, and conversations with top producers on what it actually takes to scale.",
    cover: "https://is1-ssl.mzstatic.com/image/thumb/Podcasts211/v4/6f/f7/33/6ff7334b-287f-b886-d180-3bb0af4039d8/mza_9081122532656781590.jpg/600x600bb.jpg",
    href: "https://podcasts.apple.com/us/podcast/the-millionaire-real-estate-agent-the-mrea-podcast/id1712458115",
  },
  {
    title: "Think Like A CEO",
    host: "Gary Keller & Jay Papasan",
    desc: "Big-picture business thinking from the founder of Keller Williams. Frameworks for leaders who want to scale with intention.",
    cover: "https://is1-ssl.mzstatic.com/image/thumb/Podcasts211/v4/76/b6/43/76b64340-742a-3a37-40b9-c35a96996175/mza_16973303321293303965.png/600x600bb.jpg",
    href: "https://podcasts.apple.com/us/podcast/think-like-a-ceo-with-gary-keller-jay-papasan/id1469980054",
  },
  {
    title: "The ONE Thing",
    host: "Jay Papasan",
    desc: "Extraordinary results through extraordinary focus. The companion podcast to the book that changed how we think about productivity.",
    cover: "https://is1-ssl.mzstatic.com/image/thumb/Podcasts221/v4/ef/1c/19/ef1c19a0-410c-97d6-a3be-a8ea173bc475/mza_16137425401939469791.jpg/600x600bb.jpg",
    href: "https://podcasts.apple.com/us/podcast/the-one-thing/id1191482456",
  },
  {
    title: "Empire Building",
    host: "Keller Podcast Network",
    desc: "Four powerhouse women on building wealth, business, and life on your own terms. A weekly dose of inspiration and tactics.",
    cover: "https://is1-ssl.mzstatic.com/image/thumb/Podcasts221/v4/5f/e2/ed/5fe2ed17-cf58-bf2f-548e-c50d45137999/mza_9053998687079285546.jpg/600x600bb.jpg",
    href: "https://podcasts.apple.com/us/podcast/empire-building/id1513216353",
  },
  {
    title: "Win Make Give",
    host: "Ben Kinney · Keller Podcast Network",
    desc: "Real estate agents and entrepreneurs trading the systems, scripts, and stories that move the needle in their business.",
    cover: "https://is1-ssl.mzstatic.com/image/thumb/Podcasts125/v4/d9/89/d3/d989d3ed-d5ea-d21a-a411-e19f0fba6403/mza_1144142145197360189.jpg/600x600bb.jpg",
    href: "https://podcasts.apple.com/us/podcast/win-make-give-with-ben-kinney/id1497808752",
  },
  {
    title: "Achieve Your Goals",
    host: "Hal Elrod · The Miracle Morning",
    desc: "The mornings we show up for ourselves are the days we show up best for everyone else. Hal's frameworks for building a life-changing routine.",
    cover: "https://is1-ssl.mzstatic.com/image/thumb/Podcasts114/v4/31/47/48/314748da-f30d-bb2a-250e-973ce02705e9/mza_16998572996988892830.jpg/600x600bb.jpg",
    href: "https://podcasts.apple.com/us/podcast/achieve-your-goals-with-hal-elrod/id820889267",
  },
];

const FREE = [
  { Icon: FileText, title: "First-Time Buyer Guide", desc: "A friendly 30-page walkthrough of buying your first LA home.", tag: "PDF · Free" },
  { Icon: Map, title: "LA Neighborhood Cheat Sheet", desc: "How to think about the LA submarkets — at a glance.", tag: "PDF · Free" },
  { Icon: Building2, title: "Investor Math 101", desc: "Cap rates, cash-on-cash, and the LA numbers that matter.", tag: "PDF · Free" },
  { Icon: Briefcase, title: "Agent Starter Toolkit", desc: "Scripts, templates, and rituals for your first 90 days.", tag: "PDF · Free" },
];


export default function Resources() {
  const [journal, setJournal] = useState([]);
  useEffect(() => {
    api.get("/journal").then((r) => setJournal(r.data)).catch(() => setJournal([]));
  }, []);

  return (
    <div data-testid="resources-page">
      <section className="container-edge pt-16 pb-12">
        <p className="overline">Resources</p>
        <h1 className="mt-6 text-5xl md:text-6xl lg:text-7xl max-w-4xl" style={{ fontFamily: "Playfair Display, serif" }}>
          Free guides, sharp insights, and the boring-but-vital stuff.
        </h1>
        <p className="mt-6 text-soft max-w-2xl">
          Bookmark this page. Whether you're three months from buying or three years out — there's
          something here for you.
        </p>
      </section>

      <section className="section pt-8" data-testid="resources-featured-guide">
        <SectionHeader overline="Free · No email required" title="Featured guide." />
        <div className="mt-12 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center bg-paper border border-soft p-8 md:p-12">
          <div className="lg:col-span-7">
            <p className="overline flex items-center gap-2" style={{ color: "var(--secondary)" }}>
              <FileText size={14} /> PDF · 2 pages
            </p>
            <h3
              className="mt-4 text-3xl md:text-4xl lg:text-5xl"
              style={{ fontFamily: "Playfair Display, serif" }}
              data-testid="featured-guide-title"
            >
              South Bay Homebuyer Cheat Sheet.
            </h3>
            <p className="mt-2 text-xl md:text-2xl italic text-sand" style={{ fontFamily: "Playfair Display, serif" }}>
              What can I actually afford?
            </p>
            <p className="mt-6 text-soft leading-relaxed max-w-xl">
              Your quick-reference guide for smarter home-buying decisions in the South Bay. Real
              numbers, local insights, and a printable worksheet to map out your financial
              snapshot, monthly payment comfort, cash-to-close checklist, and next steps.
              Everything you need to walk into a lender conversation prepared.
            </p>
            <ul className="mt-6 space-y-2 text-sm text-soft">
              <li className="flex items-start gap-2">
                <span className="text-sand mt-0.5">✦</span>
                What actually affects your buying power
              </li>
              <li className="flex items-start gap-2">
                <span className="text-sand mt-0.5">✦</span>
                Financial snapshot &amp; cash-to-close worksheet
              </li>
              <li className="flex items-start gap-2">
                <span className="text-sand mt-0.5">✦</span>
                Smart questions to bring to your lender
              </li>
              <li className="flex items-start gap-2">
                <span className="text-sand mt-0.5">✦</span>
                A next-steps checklist you can print and fill in
              </li>
            </ul>
            <div className="mt-8 flex flex-wrap gap-3">
              <a
                href="/downloads/south-bay-homebuyer-cheat-sheet.pdf"
                download
                className="btn-primary inline-flex"
                data-testid="featured-guide-download-pdf"
              >
                <Download size={16} className="mr-2" /> Download PDF
              </a>
              <a
                href="/downloads/south-bay-homebuyer-cheat-sheet.png"
                target="_blank"
                rel="noopener noreferrer"
                className="btn-outline inline-flex"
                data-testid="featured-guide-view-image"
              >
                View as image <ArrowUpRight size={14} className="ml-2" />
              </a>
            </div>
            <p className="mt-4 text-xs text-soft">
              Free · No email required · Print-friendly
            </p>
          </div>
          <div className="lg:col-span-5">
            <a
              href="/downloads/south-bay-homebuyer-cheat-sheet.pdf"
              download
              className="block relative overflow-hidden zoom-img group border border-soft"
              data-testid="featured-guide-cover-link"
            >
              <img
                src="/downloads/south-bay-homebuyer-cheat-sheet.png"
                alt="South Bay Homebuyer Cheat Sheet preview"
                className="w-full h-auto object-cover"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/25 transition-colors flex items-center justify-center">
                <span className="opacity-0 group-hover:opacity-100 transition-opacity bg-white text-ocean px-6 py-3 text-sm tracking-wide inline-flex items-center">
                  <Download size={14} className="mr-2" /> Download PDF
                </span>
              </div>
            </a>
          </div>
        </div>
      </section>

      <section className="section pt-8" data-testid="resources-free">
        <SectionHeader overline="Free downloads" title="Take what's useful." />
        <div className="mt-16 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {FREE.map(({ Icon, title, desc, tag }, i) => (
            <div key={title} className="editorial-card p-8 flex flex-col" data-testid={`free-resource-${i}`}>
              <Icon size={26} className="text-sand" strokeWidth={1.25} />
              <p className="overline mt-6">{tag}</p>
              <h3 className="mt-3 text-xl" style={{ fontFamily: "Playfair Display, serif" }}>{title}</h3>
              <p className="mt-3 text-soft text-sm flex-1">{desc}</p>
              <button className="mt-6 inline-flex items-center text-sm text-ocean hover:text-sand transition-colors self-start">
                Download <Download size={14} className="ml-2" />
              </button>
            </div>
          ))}
        </div>
      </section>

      <section className="section pt-8" data-testid="resources-insider-cta">
        <Link
          to="/insider-resources"
          className="relative overflow-hidden block p-10 md:p-14 group transition-all duration-300 hover:-translate-y-0.5"
          style={{ backgroundColor: "#f8f5ee", border: "1px solid var(--border)" }}
          data-testid="resources-insider-link"
        >
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
            <div className="lg:col-span-9">
              <p className="overline flex items-center gap-2" style={{ color: "var(--secondary)" }}>
                <Sparkles size={14} /> Members only · Free access
              </p>
              <h3
                className="mt-4 text-3xl md:text-4xl lg:text-5xl"
                style={{ fontFamily: "Playfair Display, serif" }}
              >
                The Insider Resources vault.
              </h3>
              <p className="mt-4 text-soft max-w-2xl leading-relaxed">
                Worksheets, neighborhood deep-dives, and playbooks we only share with people
                we've met. Instant access — takes 30 seconds.
              </p>
            </div>
            <div className="lg:col-span-3 lg:text-right">
              <span className="btn-primary inline-flex group-hover:bg-sand transition-colors">
                <Lock size={14} className="mr-2" /> Unlock <ArrowUpRight size={14} className="ml-2" />
              </span>
            </div>
          </div>
        </Link>
      </section>

      {/* Deal Room - featured at top */}
      <section className="section pt-8" data-testid="resources-dealroom">
        <div
          className="relative overflow-hidden p-10 md:p-14 text-white"
          style={{ backgroundColor: "var(--primary)" }}
        >
          <div className="absolute -right-20 -top-20 w-72 h-72 rounded-full opacity-10" style={{ backgroundColor: "#e8c79a" }} />
          <div className="relative grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
            <div className="lg:col-span-8">
              <p className="overline flex items-center gap-2" style={{ color: "#e8c79a" }}>
                <Lock size={14} /> Private · Agents only
              </p>
              <h2
                className="mt-4 text-4xl md:text-5xl lg:text-6xl text-white"
                style={{ fontFamily: "Playfair Display, serif" }}
              >
                The Deal Room.
              </h2>
              <p className="mt-6 text-white/85 max-w-xl text-base md:text-lg leading-relaxed font-light">
                A private WhatsApp group where LA's top agents share off-market listings, events,
                and resources you won't find on the MLS. Request access — Sonia adds members
                personally.
              </p>
            </div>
            <div className="lg:col-span-4 lg:text-right">
              <Link to="/deal-room" className="btn-secondary inline-flex" data-testid="resources-dealroom-cta">
                <MessageCircle size={16} className="mr-2" /> Request access <ArrowUpRight size={14} className="ml-2" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Podcasts we love */}
      <section className="section bg-linen" data-testid="resources-podcasts">
        <SectionHeader
          overline="Earbuds in"
          title="Podcasts we never miss."
          subtitle="The shows in our regular rotation — for big business thinking, daily focus, and the mindset work that makes the rest possible."
        />
        <div className="mt-16 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
          {PODCASTS.map((p, i) => (
            <a
              key={p.title}
              href={p.href}
              target="_blank"
              rel="noopener noreferrer"
              className="bg-paper border border-soft flex flex-col group transition-all duration-300 hover:border-sand hover:-translate-y-1 overflow-hidden"
              data-testid={`podcast-${i}`}
            >
              <div className="aspect-square overflow-hidden bg-linen">
                <img
                  src={p.cover}
                  alt={`${p.title} cover`}
                  loading="lazy"
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
              </div>
              <div className="p-8 flex flex-col flex-1">
                <p className="overline">{p.host}</p>
                <h3
                  className="mt-2 text-xl md:text-2xl leading-tight"
                  style={{ fontFamily: "Playfair Display, serif" }}
                >
                  {p.title}
                </h3>
                <p className="mt-3 text-soft text-sm leading-relaxed flex-1">{p.desc}</p>
                <span className="mt-6 inline-flex items-center text-sm text-ocean group-hover:text-sand transition-colors">
                  Listen on Apple Podcasts <ArrowUpRight size={14} className="ml-2" />
                </span>
              </div>
            </a>
          ))}
        </div>
      </section>

      <section className="section bg-linen" data-testid="resources-articles">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <SectionHeader overline="Notes from the team" title="The Field Journal." subtitle="Plain-spoken essays from the front lines of LA real estate." />
          </div>
          <div className="lg:col-span-8 divide-y divide-soft">
            {journal.map((a, i) => (
              <Link
                to={`/resources/journal/${a.slug}`}
                key={a.title}
                data-testid={`article-${i}`}
                className="block py-8 group"
              >
                <div className="flex items-center justify-between gap-6">
                  <h3 className="text-2xl md:text-3xl group-hover:text-sand transition-colors" style={{ fontFamily: "Playfair Display, serif" }}>
                    {a.title}
                  </h3>
                  <span className="shrink-0 text-soft text-sm flex items-center gap-3">
                    {a.read}
                    <ArrowUpRight size={18} />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="section" data-testid="resources-clientcta">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7 zoom-img min-h-[360px]">
            <img src={IMG.interior2} alt="Library" className="w-full h-full object-cover" />
          </div>
          <div className="lg:col-span-5">
            <p className="overline">Current clients only</p>
            <h2 className="mt-4 text-4xl md:text-5xl" style={{ fontFamily: "Playfair Display, serif" }}>
              The deeper library is behind a door.
            </h2>
            <p className="mt-6 text-soft">
              Vendor lists, market reports, and the in-process documents you'll actually want
              during your transaction. Enter with your client password.
            </p>
            <Link to="/client-library" className="btn-primary mt-8 inline-flex" data-testid="resources-library-cta">
              Enter client library
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
